import type { CSSProperties } from 'react';
import { cn } from '@/lib/cn';
import { formatMoney, formatWager } from '../format';
import type { Standing } from '../api';

interface PodiumProps {
  rows: Standing[];
  currency: string;
}

const displayOrder = [1, 0, 2];
const metals = ['#ebc86f', '#bfcbdc', '#c99170'] as const;

/** The same stage on the home page and race detail. Ties retain their real rank. */
export function Podium({ rows, currency }: PodiumProps) {
  if (rows.length === 0) return null;

  return (
    <div className="podium-stage">
      <div className="podium-spotlight" aria-hidden="true" />
      <ol className="podium" aria-label="Yarışma podyumu">
        {displayOrder.map((index) => {
          const row = rows[index];
          if (!row) return null;
          const medalIndex = Math.min(Math.max(row.rank - 1, 0), 2);
          const metal = metals[medalIndex] ?? metals[2];
          const hasPrize = row.prize !== null && row.prize !== undefined;
          return (
            <li
              key={`${row.rank}-${row.displayName}`}
              className={cn(
                'podium-slot',
                index === 0 && 'podium-centre',
                row.isYou && 'podium-you',
              )}
              style={
                {
                  '--medal': metal,
                  '--step-height': `${[152, 110, 82][medalIndex] ?? 82}px`,
                } as CSSProperties
              }
            >
              <div className="podium-player">
                {index === 0 ? (
                  <span className="podium-crown" aria-hidden="true">
                    <Crown />
                  </span>
                ) : null}
                <div className="podium-avatar">
                  {row.avatarUrl ? (
                    <img src={row.avatarUrl} alt="" width={80} height={80} loading="lazy" />
                  ) : (
                    <span className="podium-initial">
                      {row.displayName.slice(0, 1).toLocaleUpperCase('tr-TR')}
                    </span>
                  )}
                  <span className="podium-medal">{row.rank}</span>
                </div>
                <p className="podium-name" title={row.displayName}>
                  {row.displayName}
                  {row.isYou ? <span className="ml-1 text-xs">(sen)</span> : null}
                </p>
                <p
                  className="podium-wager"
                  aria-label={`Çevrim: ${formatWager(row.wagered, currency)}`}
                >
                  {formatWager(row.wagered, currency)}
                </p>
              </div>
              <div className="podium-step">
                <span aria-hidden="true" className="podium-step-top" />
                <div className="podium-step-front">
                  {hasPrize ? (
                    <>
                      <span className="podium-prize-label">Ödül</span>
                      <span className="podium-prize">
                        {row.prizeLabel ?? formatMoney(row.prize!, currency)}
                      </span>
                    </>
                  ) : null}
                  <span className="podium-place" aria-hidden="true">
                    {String(row.rank).padStart(2, '0')}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="podium-floor" aria-hidden="true" />
    </div>
  );
}

function Crown() {
  return (
    <svg viewBox="0 0 32 28" fill="none">
      <path d="m4 8 6 5 6-9 6 9 6-5-3 15H7L4 8Z" fill="currentColor" />
      <path d="M8 26h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="3" cy="6" r="2" fill="currentColor" />
      <circle cx="16" cy="2" r="2" fill="currentColor" />
      <circle cx="29" cy="6" r="2" fill="currentColor" />
    </svg>
  );
}
